import { useState } from "react";
import { Link } from "wouter";
import { Copy, Check, ChevronDown, ChevronRight, ExternalLink, ArrowLeft, Zap, Shield, BookOpen, Settings, Webhook, Database, Workflow, Link2, Users, BarChart3, CheckCircle2, Key, DollarSign, Building2 } from "lucide-react";
import { toast } from "sonner";

function CodeBlock({ code, language = "text" }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied!");
  };
  return (
    <div className="relative bg-[#0A1628] border border-[#84ab4a]/15 rounded-xl overflow-hidden my-3">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
        <span className="text-[10px] text-white/30 font-mono uppercase">{language}</span>
        <button onClick={copy} className="text-white/30 hover:text-[#84ab4a] transition-colors">
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <pre className="px-4 py-3 text-sm text-white/80 font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed">{code}</pre>
    </div>
  );
}

function Section({ id, icon: Icon, title, color, children }: { id: string; icon: any; title: string; color: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div id={id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden mb-5">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <span className="font-bold text-gray-900">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-4 border-b border-gray-50 last:border-0">
      <div className="w-7 h-7 rounded-full bg-[#84ab4a]/12 border border-[#84ab4a]/30 flex items-center justify-center text-xs font-bold text-[#84ab4a] flex-shrink-0 mt-0.5">{n}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 mb-1.5">{title}</div>
        <div className="text-sm text-gray-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Note({ type = "info", children }: { type?: "info" | "warning" | "success"; children: React.ReactNode }) {
  const styles = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warning: "bg-amber-50 border-amber-200 text-amber-800",
    success: "bg-green-50 border-green-200 text-green-800",
  };
  return (
    <div className={`border rounded-xl px-4 py-3 text-sm my-3 ${styles[type]}`}>{children}</div>
  );
}

const TOC = [
  { id: "overview", label: "System Overview", icon: BarChart3 },
  { id: "api-key", label: "API Key Setup", icon: Key },
  { id: "ghl-setup", label: "Wibiz Dashboard Setup", icon: Settings },
  { id: "custom-fields", label: "Custom Fields", icon: Database },
  { id: "pipeline", label: "Pipeline Configuration", icon: Workflow },
  { id: "webhook", label: "Webhook Setup", icon: Webhook },
  { id: "make-webhook", label: "Make.com Automation", icon: Zap },
  { id: "referral-links", label: "Referral Link System", icon: Link2 },
  { id: "automations", label: "Automation Workflows", icon: Zap },
  { id: "agent-sites", label: "Agent Sites (Funnels)", icon: Users },
  { id: "commission-grid", label: "Commission Grid", icon: DollarSign },
  { id: "testing", label: "Testing & Validation", icon: CheckCircle2 },
];

export default function GHLGuide() {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(key);
    setTimeout(() => setCopiedItem(null), 2000);
    toast.success("Copied!");
  };

  const webhookUrl = `${window.location.origin}/api/ghl/webhook`;

  return (
    <div className="flex min-h-screen bg-[#F5F6F8]">
      {/* Sidebar TOC */}
      <aside className="w-56 bg-[#0D1E35] flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="p-4 border-b border-white/7">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#E8620A]/15 border border-[#E8620A]/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-[#E8620A]" />
            </div>
            <div>
              <div className="text-sm font-bold text-white">Wibiz Setup Guide</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">Integration Docs</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {TOC.map(({ id, label, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium text-white/55 hover:text-white hover:bg-white/5 transition-all"
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              {label}
            </a>
          ))}
        </nav>
        <div className="p-3 border-t border-white/7">
          <Link href="/admin">
            <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-all">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Admin
            </button>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-56 flex-1">
        {/* Header */}
        <div className="bg-[#0D1E35] border-b border-white/7 px-8 py-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-xs text-white/30 mb-3">
              <Link href="/"><span className="hover:text-white/60 cursor-pointer">Portal</span></Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white/60">Wibiz Setup Guide</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Wibiz Dashboard Integration Guide</h1>
            <p className="text-white/50 text-sm leading-relaxed">
              Complete step-by-step instructions to connect BizConnect360 / Scale360 with the Wibiz Dashboard as the single source of truth. Covers API key setup, custom fields, pipeline configuration, Make.com automation, referral links, agent sites, and the full commission grid.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <span className="text-xs bg-[#84ab4a]/15 border border-[#84ab4a]/30 text-[#84ab4a] px-2.5 py-1 rounded-full font-semibold">BC360 v2.0</span>
              <span className="text-xs text-white/25">Last updated: March 2026</span>
            </div>
          </div>
        </div>

        <div className="p-8 max-w-3xl">

          {/* ── OVERVIEW ── */}
          <Section id="overview" icon={BarChart3} title="1. System Architecture Overview" color="#84ab4a">
            <div className="pt-4">
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                BizConnect360 operates as a <strong>unified CRM layer</strong> that sits on top of the Wibiz Dashboard (the CRM platform your team uses). The BC360 system handles rep management, referral tracking, and commission calculations, while the Wibiz Dashboard manages contacts, automations, appointment booking, and client communications.
              </p>

              <div className="bg-[#0D1E35] rounded-xl p-5 my-4">
                <div className="text-xs text-[#84ab4a] font-bold uppercase tracking-wider mb-3">Architecture Flow</div>
                <div className="space-y-2 text-sm">
                  {[
                    ["Client visits referral link", "scale360.wibiz.ai/?ref=S360-003", "#84ab4a"],
                    ["Lead submits form", "BC360 creates lead record + tags rep code", "#1A6FD4"],
                    ["BC360 → Wibiz Dashboard", "Contact created with rep tag via Private API", "#E8620A"],
                    ["Wibiz automation fires", "Follow-up sequence + appointment booking", "#0FA8A8"],
                    ["Stage update in Wibiz Dashboard", "Webhook back to BC360 updates pipeline", "#7B4FD4"],
                    ["Payment received", "Commission auto-calculated for rep", "#C8920A"],
                  ].map(([step, detail, color]) => (
                    <div key={step} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: color }} />
                      <div>
                        <span className="text-white/80 font-medium">{step}</span>
                        <span className="text-white/35 ml-2 text-xs">{detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">BC360 Handles</div>
                  {["Rep registration & referral IDs", "Lead pipeline (7 stages)", "Commission tracking", "Admin & rep dashboards", "Referral link generation"].map((i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600 py-1">
                      <CheckCircle2 className="w-3 h-3 text-[#84ab4a] flex-shrink-0" />{i}
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Wibiz Dashboard Handles</div>
                  {["Contact CRM & history", "Email/SMS automations", "Appointment booking", "Funnel pages (agent sites)", "Workflow triggers via Make.com"].map((i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600 py-1">
                      <CheckCircle2 className="w-3 h-3 text-[#1A6FD4] flex-shrink-0" />{i}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* ── API KEY SETUP ── */}
          <Section id="api-key" icon={Key} title="2. API Key & Credentials Setup" color="#C8920A">
            <div className="pt-4 space-y-0">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="font-semibold text-amber-800 text-sm mb-1">Where to update your API key in BC360</div>
                <p className="text-sm text-amber-700">
                  In the BC360 Management UI, go to <strong>Settings → Secrets</strong>. Update the following two secrets:
                </p>
                <div className="mt-3 space-y-2">
                  <div className="bg-white border border-amber-200 rounded-lg px-4 py-2.5">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-bold text-gray-800">GHL_PRIVATE_API_KEY</code>
                      <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">REQUIRED</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Your Private Integration API key from the Wibiz Dashboard → Settings → Integrations → Private Integrations</p>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg px-4 py-2.5">
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-bold text-gray-800">MAKE_WEBHOOK_URL</code>
                      <span className="text-[10px] bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded-full font-bold">RECOMMENDED</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Your Make.com scenario webhook URL for Scale360 pipeline automation</p>
                  </div>
                </div>
                <div className="mt-3 p-2.5 bg-[#84ab4a]/8 border border-[#84ab4a]/20 rounded-lg">
                  <p className="text-xs text-gray-700">Location ID (pre-configured in server): <code className="font-mono font-bold text-[#84ab4a]">4NIo95IzKUVayysfr0PX</code></p>
                </div>
              </div>
              <Step n={1} title="Generate a Private Integration API Key">
                Log into the Wibiz Dashboard. Go to <strong>Settings → Integrations → Private Integrations → Create New Integration</strong>. Name it "BizConnect360 Dashboard". Enable scopes: <code className="bg-gray-100 px-1 rounded text-xs">contacts.write</code>, <code className="bg-gray-100 px-1 rounded text-xs">opportunities.write</code>, <code className="bg-gray-100 px-1 rounded text-xs">contacts.readonly</code>, <code className="bg-gray-100 px-1 rounded text-xs">opportunities.readonly</code>. Copy the generated key.
              </Step>
              <Step n={2} title="Add the API Key to BC360 Secrets">
                In the BC360 Management UI sidebar, click <strong>Settings → Secrets</strong>. Find <code className="bg-gray-100 px-1 rounded text-xs">GHL_PRIVATE_API_KEY</code> and paste your key. Save. The server will automatically use it for all Wibiz Dashboard API calls.
              </Step>
              <Step n={3} title="Verify the Connection">
                After adding the key, go to <strong>Admin Dashboard → Wibiz Sync tab</strong>. You should see a green 'Connected' status and your Location ID confirmed.
              </Step>
            </div>
          </Section>

          {/* ── GHL SETUP ── */}
          <Section id="ghl-setup" icon={Settings} title="3. Wibiz Dashboard Account Setup" color="#1A6FD4">
            <div className="pt-4 space-y-0">
              <Step n={1} title="Access Your Wibiz Dashboard Sub-Account">
                Log into the Wibiz Dashboard. Navigate to your BizConnect360 sub-account (not the agency-level account). All configuration should be done at the sub-account level with Location ID <code className="bg-gray-100 px-1 rounded text-xs">4NIo95IzKUVayysfr0PX</code>.
              </Step>
              <Step n={2} title="Enable Required Integrations">
                Go to <strong>Settings → Integrations</strong> and ensure the following are enabled: <em>Webhooks</em>, <em>Forms</em>, <em>Calendars</em>, and <em>Pipelines</em>. These are required for the BC360 integration to function.
              </Step>
              <Step n={3} title="Set Up Your Business Profile">
                Under <strong>Settings → Business Profile</strong>, confirm your business name, timezone, and contact information are accurate. This affects all automated communications sent to leads.
              </Step>
              <Step n={4} title="Configure Email & SMS Sending">
                Under <strong>Settings → Email Services</strong>, connect your sending domain (e.g., noreply@scale360.wibiz.ai). For SMS, connect your Twilio or LC Phone number. Both are required for automated follow-up sequences.
              </Step>
            </div>
          </Section>

          {/* ── CUSTOM FIELDS ── */}
          <Section id="custom-fields" icon={Database} title="4. Custom Fields Configuration" color="#E8620A">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Custom fields are critical for tracking rep attribution and vertical assignment. Create these fields in the Wibiz Dashboard under <strong>Settings → Custom Fields → Contacts</strong>.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-100 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Field Name</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Field Key</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Rep Code", "rep_code", "Text", "Stores the referring rep's ID (e.g. S360-003)"],
                      ["BC360 Lead ID", "bc360_lead_id", "Text", "Links Wibiz contact to BC360 database record"],
                      ["BC360 Stage", "bc360_stage", "Dropdown", "Current pipeline stage in BC360"],
                      ["Vertical", "bc360_vertical", "Dropdown", "Which BC360 vertical (Fundable360, etc.)"],
                      ["Plan", "bc360_plan", "Dropdown", "Lite / Standard / Pro"],
                      ["Setup Fee", "bc360_setup_fee", "Number", "One-time setup fee amount"],
                      ["Monthly Fee", "bc360_monthly_fee", "Number", "Recurring monthly fee amount"],
                      ["Referral Source", "referral_source", "Text", "How the lead found BC360"],
                    ].map(([name, key, type, purpose]) => (
                      <tr key={key} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-4 py-2.5 font-semibold text-gray-800">{name}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-[#E8620A]">{key}</td>
                        <td className="px-4 py-2.5 text-gray-500">{type}</td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Note type="info">
                After creating the <code className="bg-blue-100 px-1 rounded text-xs">rep_code</code> field, note its Field ID from the URL (e.g., <code className="bg-blue-100 px-1 rounded text-xs">aVt0KNcl5azG2Rdx7oFC</code>). You'll need this when configuring webhook payloads.
              </Note>

              <Step n={1} title="Create Dropdown Options for bc360_stage">
                Add these exact values to the bc360_stage dropdown field:
                <CodeBlock language="values" code={`lead_captured
audit_submitted
follow_up_active
payment_received
onboarding_submitted
active_client
lost_cold`} />
              </Step>

              <Step n={2} title="Create Dropdown Options for bc360_vertical">
                <CodeBlock language="values" code={`Fundable360
Share360
Scale360
Legacy360
Build360
Protect360`} />
              </Step>
            </div>
          </Section>

          {/* ── PIPELINE ── */}
          <Section id="pipeline" icon={Workflow} title="5. Pipeline Configuration" color="#0FA8A8">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Create a dedicated pipeline in the Wibiz Dashboard that mirrors the BC360 7-stage pipeline. Go to <strong>CRM → Pipelines → Add Pipeline</strong>.
              </p>

              <Step n={1} title='Create Pipeline Named "BC360 Main Pipeline"'>
                Name it exactly <strong>BC360 Main Pipeline</strong>. This name is referenced in automation workflows and Make.com scenarios.
              </Step>

              <Step n={2} title="Add These 7 Stages in Order">
                <div className="mt-2 space-y-1.5">
                  {[
                    ["Lead Captured", "#1A6FD4", "Initial contact — lead submitted via referral link or rep"],
                    ["Audit Submitted", "#C8920A", "Business audit form completed by the prospect"],
                    ["Follow-Up Active", "#E8620A", "Active follow-up sequence running, calls scheduled"],
                    ["Payment Received", "#0FA8A8", "Setup fee payment confirmed"],
                    ["Onboarding Submitted", "#7B4FD4", "Onboarding form completed, account setup in progress"],
                    ["Active Client", "#84ab4a", "Client is live and using BC360 services"],
                    ["Lost / Cold", "#D93025", "Deal lost or prospect gone cold"],
                  ].map(([name, color, desc]) => (
                    <div key={name} className="flex items-start gap-3 bg-gray-50 rounded-lg p-2.5">
                      <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: color }} />
                      <div>
                        <span className="font-semibold text-gray-800 text-sm">{name}</span>
                        <span className="text-gray-400 text-xs ml-2">{desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Step>

              <Step n={3} title="Enable Stage-Change Webhooks">
                For each stage, click the stage settings gear icon and enable <strong>"Trigger webhook on stage change"</strong>. Set the webhook URL to your BC360 endpoint (configured in the next section).
              </Step>

              <Note type="warning">
                Opportunity names in Wibiz should follow the format: <strong>[Business Name] — [Vertical] — [Rep Code]</strong> (e.g., "Acme Corp — Fundable360 — S360-003"). This ensures contacts are correctly labeled by vertical and rep in the BC360 dashboard.
              </Note>
            </div>
          </Section>

          {/* ── WEBHOOK ── */}
          <Section id="webhook" icon={Webhook} title="6. Webhook Setup (Wibiz ↔ BC360)" color="#7B4FD4">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Webhooks are the backbone of the BC360 ↔ Wibiz Dashboard integration. They allow real-time data sync in both directions.
              </p>

              {/* Inbound webhook */}
              <div className="bg-[#0D1E35] rounded-xl p-4 mb-4">
                <div className="text-xs text-[#84ab4a] font-bold uppercase tracking-wider mb-2">BC360 Inbound Webhook URL (from Wibiz Dashboard)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-sm text-white/80 bg-white/5 rounded-lg px-3 py-2 truncate">{webhookUrl}</code>
                  <button
                    onClick={() => copy(webhookUrl, "webhook")}
                    className="flex-shrink-0 bg-[#84ab4a] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[#6e9038] transition-colors"
                  >
                    {copiedItem === "webhook" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              <Step n={1} title="Add Inbound Webhook in Wibiz Dashboard">
                Go to <strong>Settings → Integrations → Webhooks → Add New Webhook</strong>. Paste the BC360 webhook URL above. Select these events to send:
                <ul className="mt-2 space-y-1 text-xs text-gray-500 list-disc list-inside">
                  <li><code>ContactCreate</code> — when a new contact is added</li>
                  <li><code>ContactUpdate</code> — when contact data changes</li>
                  <li><code>OpportunityStageUpdate</code> — when pipeline stage changes</li>
                  <li><code>AppointmentCreate</code> — when appointment is booked</li>
                  <li><code>FormSubmission</code> — when a form is submitted</li>
                </ul>
              </Step>

              <Step n={2} title="Configure the Webhook Payload">
                Ensure the webhook payload includes the <code className="bg-gray-100 px-1 rounded text-xs">rep_code</code> custom field. Wibiz sends custom fields in the <code className="bg-gray-100 px-1 rounded text-xs">customFields</code> array. The BC360 webhook handler automatically extracts this.
                <CodeBlock language="json" code={`{
  "type": "ContactCreate",
  "contact": {
    "id": "ghl_contact_id_here",
    "firstName": "John",
    "lastName": "Smith",
    "email": "john@company.com",
    "phone": "555-0100",
    "companyName": "Acme Corp",
    "tags": ["rep:S360-003", "vertical:Fundable360"],
    "customFields": [
      { "key": "rep_code", "value": "S360-003" },
      { "key": "bc360_vertical", "value": "Fundable360" }
    ]
  }
}`} />
              </Step>

              <Step n={3} title="Test the Webhook">
                After saving, click <strong>"Send Test"</strong> in the Wibiz Dashboard. Then check the BC360 Admin Dashboard → Wibiz Sync tab to verify the event was received and processed.
              </Step>

              <Step n={4} title="Set Up Outbound Webhook (BC360 → Wibiz Dashboard)">
                When a rep submits a lead through BC360, the system automatically creates a contact in the Wibiz Dashboard via the Private API. Additionally, BC360 fires your Make.com webhook (see Section 7) which can trigger further Wibiz Dashboard automations.
                <Note type="info">
                  BC360 uses the Private Integration API (not webhooks) to push contacts directly to the Wibiz Dashboard. The Make.com webhook handles all downstream automation triggers.
                </Note>
              </Step>
            </div>
          </Section>

          {/* ── REFERRAL LINKS ── */}
          <Section id="referral-links" icon={Link2} title="6. Referral Link System" color="#C8920A">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Every rep gets a unique referral link in the format <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">https://scale360.wibiz.ai/?ref=S360-XXX</code>. This link is auto-generated when you register a rep in the BC360 Admin Dashboard.
              </p>

              <Step n={1} title="Register a Rep in BC360 Admin">
                Go to <strong>Admin Dashboard → Reps tab → Register New Rep</strong>. Fill in the rep's details. The system automatically:
                <ul className="mt-2 space-y-1 text-xs text-gray-500 list-disc list-inside">
                  <li>Generates the next sequential rep code (S360-001, S360-002, etc.)</li>
                  <li>Creates the referral link: <code className="bg-gray-100 px-1 rounded">https://scale360.wibiz.ai/?ref=S360-XXX</code></li>
                  <li>Stores the rep in the database with their profile</li>
                </ul>
              </Step>

              <Step n={2} title="Share the Referral Link">
                The rep can find their referral link in their Agent Dashboard (top of the page and in the sidebar). They can copy it with one click and share via email, social media, or text message.
              </Step>

              <Step n={3} title="How the Link Tracks Leads">
                When a prospect visits the referral link, the <code className="bg-gray-100 px-1 rounded text-xs">?ref=S360-XXX</code> parameter is captured by the BC360 landing page. When the prospect submits the form, their lead record is automatically tagged with the rep's code.
              </Step>

              <Step n={4} title="Replicate Agent Sites in Wibiz">
                For each rep, create a Wibiz funnel page (see Section 8) that pre-fills the rep code. The URL structure should be:
                <CodeBlock language="url" code={`https://your-wibiz-funnel.com/apply?rep_code=S360-003
# OR using Wibiz's custom domain:
https://scale360.wibiz.ai/apply?ref=S360-003`} />
              </Step>

              <Note type="success">
                The BC360 referral link system and Wibiz funnel pages work together. You can use either URL format — both capture the rep code and tag it to the lead in both BC360 and Wibiz.
              </Note>
            </div>
          </Section>

          {/* ── MAKE.COM ── */}
          <Section id="make-webhook" icon={Zap} title="7. Make.com Automation Setup" color="#7B4FD4">
            <div className="pt-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                <div className="font-semibold text-purple-800 text-sm mb-1">You already have a working Make.com workflow for Scale360</div>
                <p className="text-sm text-purple-700">BC360 will trigger it automatically on every lead creation and stage change. Add your Make.com webhook URL to the <code className="bg-purple-100 px-1 rounded">MAKE_WEBHOOK_URL</code> secret in BC360 Settings.</p>
              </div>
              <Step n={1} title="Get Your Make.com Webhook URL">
                Open Make.com → your Scale360 scenario → click the webhook trigger module → copy the URL (starts with <code className="bg-gray-100 px-1 rounded text-xs">https://hook.make.com/...</code>). Add it to BC360 Secrets as <code className="bg-gray-100 px-1 rounded text-xs">MAKE_WEBHOOK_URL</code>.
              </Step>
              <Step n={2} title="BC360 Sends This Payload to Make.com">
                <CodeBlock language="json" code={`{
  "event": "lead_created",
  "timestamp": "2026-03-10T08:00:00Z",
  "lead": {
    "id": 42,
    "firstName": "John",
    "lastName": "Smith",
    "email": "john@company.com",
    "phone": "555-0100",
    "businessName": "Acme Corp",
    "vertical": "Scale360",
    "plan": "standard",
    "repCode": "S360-001",
    "agentLevel": "Senior Associate",
    "referralLink": "https://scale360.wibiz.ai/?ref=S360-001",
    "stage": "lead_captured",
    "source": "referral_link"
  }
}`} />
              </Step>
              <Step n={3} title="Stage Change Payload">
                <CodeBlock language="json" code={`{
  "event": "stage_changed",
  "lead": {
    "id": 42,
    "repCode": "S360-001",
    "vertical": "Scale360",
    "fromStage": "audit_submitted",
    "toStage": "payment_received",
    "changedBy": "admin"
  },
  "commission": {
    "triggered": true,
    "amount": 750,
    "type": "enrollment",
    "agentLevel": "Senior Associate"
  }
}`} />
              </Step>
              <Step n={4} title="Recommended Make.com Actions per Trigger">
                <div className="space-y-2 mt-2">
                  {[
                    { trigger: "lead_created", action: "Create contact in Wibiz Dashboard with rep_code custom field" },
                    { trigger: "stage → payment_received", action: "Create opportunity in BC360 pipeline + notify rep of commission" },
                  { trigger: "stage → active_client", action: "Send welcome email + notify rep of activation" },
                  { trigger: "stage → lost_cold", action: "Tag contact in Wibiz Dashboard as Lost + notify admin" },
                  ].map(({ trigger, action }) => (
                    <div key={trigger} className="bg-gray-50 rounded-xl p-3">
                      <div className="text-xs font-bold text-[#7B4FD4] mb-1">Trigger: {trigger}</div>
                      <div className="text-xs text-gray-700">→ {action}</div>
                    </div>
                  ))}
                </div>
              </Step>
            </div>
          </Section>

          {/* ── AUTOMATIONS ── */}
          <Section id="automations" icon={Zap} title="8. Automation Workflows in Wibiz Dashboard" color="#84ab4a">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Build these workflows in <strong>Wibiz Dashboard → Automations → Workflows</strong>. Each workflow handles a specific stage in the BC360 pipeline.
              </p>

              {[
                {
                  name: "Workflow 1: New Lead Welcome Sequence",
                  trigger: "Contact Created OR Form Submission (BC360 referral form)",
                  actions: [
                    "Wait 2 minutes",
                    "Send SMS: 'Hi [First Name], thanks for your interest in BizConnect360! We'll be in touch within 24 hours. — [Rep Name]'",
                    "Send Email: Welcome email with BC360 overview PDF",
                    "Create opportunity in BC360 Main Pipeline → Stage: Lead Captured",
                    "Notify rep via email/SMS that a new lead came in under their referral code",
                    "Update custom field bc360_stage = lead_captured",
                  ],
                },
                {
                  name: "Workflow 2: Audit Form Follow-Up",
                  trigger: "Opportunity stage changes to 'Audit Submitted'",
                  actions: [
                    "Send confirmation email: 'Your audit has been received!'",
                    "Assign to sales rep (use rep_code custom field to route)",
                    "Create task for rep: 'Review audit and schedule call within 24 hours'",
                    "Start 3-day follow-up sequence if no response",
                    "Update bc360_stage = audit_submitted",
                  ],
                },
                {
                  name: "Workflow 3: Payment Received → Onboarding",
                  trigger: "Opportunity stage changes to 'Payment Received'",
                  actions: [
                    "Send payment confirmation email",
                    "Send onboarding form link",
                    "Notify admin: 'New payment received — [Contact Name] — [Plan]'",
                    "Trigger BC360 webhook to calculate and create commission record",
                    "Update bc360_stage = payment_received",
                  ],
                },
                {
                  name: "Workflow 4: Active Client Onboarding",
                  trigger: "Opportunity stage changes to 'Active Client'",
                  actions: [
                    "Send welcome-to-the-family email with login credentials",
                    "Add contact to 'Active Clients' smart list",
                    "Schedule 30-day check-in call",
                    "Trigger monthly commission calculation in BC360",
                    "Update bc360_stage = active_client",
                  ],
                },
              ].map((wf) => (
                <div key={wf.name} className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-3">
                  <div className="font-bold text-gray-900 text-sm mb-2">{wf.name}</div>
                  <div className="text-xs text-gray-500 mb-2"><strong>Trigger:</strong> {wf.trigger}</div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Actions:</div>
                  <div className="space-y-1">
                    {wf.actions.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="text-[#84ab4a] font-bold flex-shrink-0">{i + 1}.</span>
                        {a}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ── AGENT SITES ── */}
          <Section id="agent-sites" icon={Users} title="9. Agent Sites (Funnel Replication)" color="#1A6FD4">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Each rep gets their own version of the BC360 landing page, pre-tagged with their referral code. In the Wibiz Dashboard, this is done using funnel cloning and URL parameters.
              </p>

              <Step n={1} title="Create the Master Funnel Template">
                Build the master BC360 lead capture funnel in <strong>Wibiz Dashboard → Funnels & Websites → Funnels</strong>. Design it to match the BC360 landing page. Include a hidden field for <code className="bg-gray-100 px-1 rounded text-xs">rep_code</code>.
              </Step>

              <Step n={2} title="Use URL Parameters to Pre-Fill Rep Code">
                Wibiz Dashboard funnels support URL parameter pre-filling. Add a hidden form field named <code className="bg-gray-100 px-1 rounded text-xs">rep_code</code> and configure it to auto-fill from the URL parameter <code className="bg-gray-100 px-1 rounded text-xs">?ref</code>:
                <CodeBlock language="javascript" code={`// In Wibiz Dashboard funnel custom code block, add this script:
window.addEventListener('DOMContentLoaded', function() {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  if (refCode) {
    // Find the hidden rep_code field and set its value
    const repField = document.querySelector('input[name="rep_code"]');
    if (repField) repField.value = refCode;
    
    // Also store in sessionStorage for multi-step funnels
    sessionStorage.setItem('bc360_ref', refCode);
  }
});`} />
              </Step>

              <Step n={3} title="Clone Funnel for Each Rep (Optional)">
                For reps who want a fully personalized page (with their photo and bio), clone the master funnel and update the rep-specific content. The clone's URL will be:
                <CodeBlock language="url" code={`https://scale360.wibiz.ai/[rep-name]
# OR use the master funnel with URL param:
https://scale360.wibiz.ai/apply?ref=S360-003`} />
              </Step>

              <Step n={4} title="Connect Form Submission to BC360">
                In the funnel form settings, add a webhook action on form submission. Point it to the BC360 webhook URL. This ensures every form submission from the agent's Wibiz Dashboard funnel is captured in BC360 and tagged to the correct rep.
              </Step>

              <Note type="success">
                All activity on the agent's Wibiz Dashboard site (form submissions, page views, appointments) will feed back into BC360 via webhooks and be tagged to the agent's rep code and vertical.
              </Note>
            </div>
          </Section>

          {/* ── COMMISSION GRID ── */}
          <Section id="commission-grid" icon={DollarSign} title="10. Commission Grid (All Verticals)" color="#C8920A">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">Full commission structure from the BizConnect360 Commission Grid document. All percentages apply to enrollment fee or monthly subscription.</p>
              {[
                { vertical: "Fundable360", color: "#84ab4a", note: "Enrollment fee $2,995", rows: [
                  { level: "Agency", enrollment: "30% ($900)", recurring: "30% of funding splits" },
                  { level: "Senior Associate", enrollment: "25% ($750)", recurring: "25% of funding splits" },
                  { level: "Associate", enrollment: "20% ($600)", recurring: "20% of funding splits" },
                ]},
                { vertical: "Scale360", color: "#E8620A", note: "BC360 retains 50%", rows: [
                  { level: "Agency", enrollment: "40% of enrollment", recurring: "40% of subscriptions" },
                  { level: "Senior Associate", enrollment: "30% of enrollment", recurring: "30% of subscriptions" },
                  { level: "Associate", enrollment: "20% of enrollment", recurring: "20% of subscriptions" },
                ]},
                { vertical: "Legacy360", color: "#C8920A", note: "Living Trust — Enrollment fee $2,995", rows: [
                  { level: "Agency", enrollment: "30% ($900)", recurring: "N/A" },
                  { level: "Senior Associate", enrollment: "25% ($750)", recurring: "N/A" },
                  { level: "Associate", enrollment: "20% ($600)", recurring: "N/A" },
                ]},
                { vertical: "Share360", color: "#1A6FD4", note: "PENDING — Rates not yet confirmed. Commissions paid directly by banking partners.", rows: [
                  { level: "Agency", enrollment: "PENDING", recurring: "PENDING" },
                  { level: "Senior Associate", enrollment: "PENDING", recurring: "PENDING" },
                  { level: "Associate", enrollment: "PENDING", recurring: "PENDING" },
                ]},
                { vertical: "Media360", color: "#0FA8A8", note: "No commission — featured placement only", rows: [
                  { level: "Agency", enrollment: "N/A", recurring: "N/A" },
                  { level: "Senior Associate", enrollment: "N/A", recurring: "N/A" },
                  { level: "Associate", enrollment: "N/A", recurring: "N/A" },
                ]},
              ].map(({ vertical, color, rows }) => (
                <div key={vertical} className="mb-4 bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                  <div className="px-4 py-3 flex items-center gap-2" style={{ borderLeft: `3px solid ${color}` }}>
                    <Building2 className="w-4 h-4" style={{ color }} />
                    <span className="font-bold text-gray-900 text-sm">{vertical}</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-y border-gray-100">
                      <th className="text-left px-4 py-2 font-bold text-gray-400 uppercase tracking-wide">Level</th>
                      <th className="text-left px-4 py-2 font-bold text-gray-400 uppercase tracking-wide">Enrollment</th>
                      <th className="text-left px-4 py-2 font-bold text-gray-400 uppercase tracking-wide">Recurring</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(({ level, enrollment, recurring }) => (
                        <tr key={level} className="border-b border-gray-50">
                          <td className="px-4 py-2.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              level === "Agency" ? "bg-yellow-100 text-yellow-800" :
                              level === "Senior Associate" ? "bg-blue-100 text-blue-800" :
                              "bg-purple-100 text-purple-800"
                            }`}>{level}</span>
                          </td>
                          <td className="px-4 py-2.5 font-semibold text-gray-800">{enrollment}</td>
                          <td className="px-4 py-2.5 text-gray-600">{recurring}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </Section>

          {/* ── TESTING ── */}
          <Section id="testing" icon={CheckCircle2} title="11. Testing & Validation Checklist" color="#0FA8A8">
            <div className="pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Before going live, complete this validation checklist to ensure all systems are connected correctly.
              </p>

              <div className="space-y-2">
                {[
                  { cat: "Rep Registration", items: ["Register a test rep in BC360 Admin → confirm rep code is generated (S360-XXX format)", "Verify referral link appears correctly: https://scale360.wibiz.ai/?ref=S360-XXX", "Confirm rep appears in leaderboard table with correct data"] },
                  { cat: "Lead Submission (BC360)", items: ["Submit a test lead via the BC360 landing page (/join?ref=S360-001)", "Verify lead appears in Admin Pipeline under 'Lead Captured' stage", "Verify lead is tagged with the correct rep code", "Check that rep's lead count increments in the leaderboard"] },
                  { cat: "Wibiz Dashboard Webhook", items: ["Confirm BC360 webhook URL is configured in Wibiz Dashboard Settings → Webhooks", "Trigger a ContactCreate event and verify it appears in BC360 Wibiz Sync tab", "Verify rep_code is extracted from the payload correctly", "Test stage change webhook: move opportunity in pipeline and confirm BC360 updates"] },
                  { cat: "Make.com Automation", items: ["MAKE_WEBHOOK_URL added to BC360 Secrets", "Test lead created and verified in Make.com scenario history", "Stage change (payment_received) triggers commission notification in Make.com"] },
                  { cat: "Rep Dashboard", items: ["Log into Rep Dashboard with a test rep code", "Confirm personal leads are displayed (filtered by rep code)", "Verify referral link is shown and copyable", "Submit a lead from the rep dashboard and confirm it appears in the pipeline"] },
                  { cat: "Commissions", items: ["Move a test lead to 'Payment Received' stage", "Verify commission record is created in BC360", "Confirm commission appears in rep's dashboard under Commissions tab", "Test admin approve/pay commission workflow"] },
                ].map(({ cat, items }) => (
                  <div key={cat} className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                    <div className="font-bold text-gray-800 text-sm mb-2">{cat}</div>
                    <div className="space-y-1.5">
                      {items.map((item) => (
                        <label key={item} className="flex items-start gap-2.5 cursor-pointer group">
                          <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-[#84ab4a] focus:ring-[#84ab4a]/30 flex-shrink-0" />
                          <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">{item}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Note type="success">
                Once all checklist items are verified, your BC360 ↔ Wibiz Dashboard integration is fully operational. All rep activity, lead submissions, stage updates, and commissions will flow seamlessly between both systems.
              </Note>
            </div>
          </Section>

          {/* Quick Reference */}
          <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 mt-6">
            <div className="text-sm font-bold text-[#84ab4a] uppercase tracking-wider mb-4">Quick Reference</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wide mb-2">Key URLs</div>
                {[
                  ["Admin Dashboard", "/admin"],
                  ["Rep Dashboard", "/rep"],
                  ["Client Landing", "/join?ref=S360-XXX"],
                  ["Wibiz Webhook", "/api/ghl/webhook"],
                ].map(([label, url]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/5">
                    <span className="text-white/60 text-xs">{label}</span>
                    <code className="text-[#84ab4a] text-xs font-mono">{url}</code>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wide mb-2">Commission Rates</div>
                {[
                  ["Agency", "30-40% enrollment + recurring"],
                  ["Senior Associate", "25-30% enrollment + recurring"],
                  ["Associate", "20% enrollment + recurring"],
                  ["Location ID", "4NIo95IzKUVayysfr0PX"],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-white/5">
                    <span className="text-white/60 text-xs">{label}</span>
                    <span className="text-[#84ab4a] text-xs font-semibold">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-8 pb-8">
            <p className="text-xs text-gray-400">BizConnect360 Wibiz Dashboard Integration Guide · Scale360 · Wibiz · v2.0</p>
            <p className="text-xs text-gray-300 mt-1">For support, contact your BC360 technical team</p>
          </div>
        </div>
      </main>
    </div>
  );
}
